import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import IconButton from "../../../../../components/base/button/icon_button";
import {Box, Paper, SvgIcon, Tooltip, Typography, useTheme} from "@mui/material";
import {ReactComponent as Share} from "../../../../../../assets/images/share.svg";
import {ReactComponent as Competitors} from "../../../../../../assets/images/lookup-part-info/competitors.svg";
import {ReactComponent as Report} from "../../../../../../assets/images/report.svg";
import {ReactComponent as Price} from "../../../../../../assets/images/sidebar/price.svg";
import {ArrowBack, EditOutlined, QueryStatsRounded} from "@mui/icons-material";
import {formatMoney, getOffsetTop,} from "../../../../../../core/services/utils/utils";
import LookupProductInformationViewBasicSection from "./sections/basic";
import ManagePartShareLinkDialog from "./modal/share_part";
import ReportProblemModal from "./modal/report_problem";
import ReportCompetitorPriceDialog from "../../../../../components/dialogs/report-competitor-price";
import Divider from "@mui/material/Divider";
import PartScrollButton from "../../../../../components/app-specific/scroll_button";
import LookupProductInformationViewVisitLogsSection from "./sections/visit-logs";
import LookupProductInformationViewInfoSection from "./sections/info";
import LookupProductInformationViewFeedbacksSection from "./sections/feedbacks";
import LookupProductInformationViewRelatedPartsSection from "./sections/relations";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ElementIds from "../../../../../../core/models/element-ids";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {Link, useHistory, useParams} from "react-router-dom";
import useQuery from "../../../../../hooks/use-query";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import ProductEntryHistoryDialog from "../../../../../components/dialogs/product-entry-history";
import Api from "../../../../../../core/services/api/api";
import useCachedPriceLevelsDictionaryController from "../../../../../hooks/use-price-level-dictionary";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import DateUtils from "../../../../../../core/services/date-utils";

const PartSections = {
    basic: 'basic',
    visitLogs: 'visitLogs',
    info: 'info',
    feedbacks: 'feedbacks',
    relatedParts: 'relatedParts',
}

const PartInformationLookupSingleViewSections = [
    {[PartSections.basic]: 'Basic'},
    {[PartSections.info]: 'Information'},
    {[PartSections.visitLogs]: 'Visit Logs'},
    {[PartSections.relatedParts]: 'Related Parts'},
    {[PartSections.feedbacks]: 'Feedbacks'},
];

const LookupProductInformationView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.lookup-product-information'});
    const operatorAccessLevels = useAccessLevels();
    const history = useHistory();
    const params = useParams();
    const [query] = useQuery();

    const [showCompetitorsPrice, setShowCompetitorsPrice] = useState(false);
    const [showPrices, setShowPrices] = useState(false);
    const [showProblem, setShowProblem] = useState(false);
    const [showLink, setShowLink] = useState(false);
    const [openPriceHistoryChart, setOpenPriceHistoryChart] = useState(false);

    const [part, setPart] = useState({});
    const [partLoading, setPartLoading] = useState(true);

    const [sections, setSections] = useState(PartInformationLookupSingleViewSections);
    const [selectedSection, setSelectedSection] = useState(PartSections.basic);

    const {configureNotebookTags} = useConfigureNotebook('lookup-product-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const scrolling = useRef(false);
    const sectionsRef = useRef(sections);
    sectionsRef.current = sections;

    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();
    const isMounted = useIsMounted();

    const customer = useMemo(() => query.customer ? JSON.parse(query.customer) : null, [query.customer])

    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])
    const partNumber = useMemo(() => decodeURIComponent(params?.partNumber), [params?.partNumber]);


    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.lookupProductInformation(params?.partNumber));
        configureBehavioralTool((tags) => tags.lookupProductInformation(params?.partNumber));
    }, [configureBehavioralTool, configureNotebookTags, params?.partNumber]);

    /**
     * Sets the selected section as the user clicks on a section to scroll to.
     *
     * Gets the top of each section with respect to the client window (visible screen)
     * if there is an element with a top of 0 or more, selects that section.
     *
     * @type {function(): undefined}
     */
    const onScroll = useCallback(() => {
        if (scrolling.current)
            return;

        const sections = sectionsRef.current;
        if (!sections.length) {
            return setSelectedSection(null);
        }
        const mainHeightMid = Math.round(document.getElementById(ElementIds.panel.main).getBoundingClientRect().height / 2);
        const tops = sections.map(e => document.getElementById(Object.keys(e)[0]).getBoundingClientRect().top);
        const topMost = tops.findIndex(e => e >= 0);
        if (topMost === -1) {
            // the case where all the sections tops are above the window => at the end of the window vertically
            const lastSection = sections[sections.length - 1];
            setSelectedSection(prevState => {
                if (lastSection !== prevState)
                    return Object.keys(lastSection)[0];
                return prevState;
            })
        } else {
            if (tops[topMost] <= mainHeightMid) {
                const newSelected = sections[topMost];
                setSelectedSection(prevState => {
                    if (newSelected !== prevState)
                        return Object.keys(newSelected)[0];
                    return prevState;
                })
            }
        }
    }, [])

    /**
     * With each change in the partNumber value of the url params:
     * - if the component was mounted prior to the change, then resets the sections.
     */
    useEffect(() => {
        onScroll();
    }, [onScroll])

    /**
     * Attaches a scroll event listener for the main section of the website so that if the user scrolls, the proper
     * section gets selected
     */
    useEffect(() => {
        const main = document.getElementById(ElementIds.panel.main);
        main?.addEventListener('scroll', onScroll);
        return () => main?.removeEventListener('scroll', onScroll);
    }, [onScroll])

    /**
     * Waits for 0.5 seconds after the creation of the page and then scrolls the user to the top of the page
     */
    useEffect(() => {
        if (!part) return;
        const timer = setTimeout(() => {
            document.getElementById(ElementIds.panel.main)?.scrollTo(0, 0);
        }, 0);
        return () => clearTimeout(timer);
    }, [part])


    /**
     * Scrolls the user to the selected section.
     *
     * Gets the height of the topBar and scrolls the window to the top fo the selected section - the height of the
     * topBar so that nothing gets behind the top section
     * @param section
     */
    const scrollToSection = (section) => {
        scrolling.current = true;
        const topBarHeight = document.getElementById(ElementIds.panel.appBar).getBoundingClientRect().height + 30;
        const mainSection = document.getElementById(ElementIds.panel.main);
        switch (section) {
            case PartSections.basic:
            default:
                mainSection?.scrollTo(0, getOffsetTop(document.getElementById(PartSections.basic), ElementIds.panel.main) - topBarHeight)
                break;
            case PartSections.info:
                mainSection?.scrollTo(0, getOffsetTop(document.getElementById(PartSections.info), ElementIds.panel.main) - topBarHeight)
                break;
            case PartSections.relatedParts:
                mainSection?.scrollTo(0, getOffsetTop(document.getElementById(PartSections.relatedParts), ElementIds.panel.main) - topBarHeight)
                break;
            case PartSections.feedbacks:
                mainSection?.scrollTo(0, getOffsetTop(document.getElementById(PartSections.feedbacks), ElementIds.panel.main) - topBarHeight)
                break;
            case PartSections.visitLogs:
                mainSection?.scrollTo(0, getOffsetTop(document.getElementById(PartSections.visitLogs), ElementIds.panel.main) - topBarHeight)
                break;
        }
        scrolling.current = false;
    }

    /**
     * Opens modal for competitors price
     */
    const reportCompetitorPrice = useCallback(() => {
        setShowCompetitorsPrice(true);
    }, [])

    /**
     * Shows modal for reporting a problem
     */
    const reportProblem = useCallback(() => {
        setShowProblem(true);
    }, [])

    /**
     * Removes the given section from the view.
     * @param {string} sectionKey
     */
    const removeSection = useCallback((sectionKey) => {
        setSections(prevState => prevState?.filter(e => Object.keys(e)[0] !== sectionKey));
    }, [])

    /**
     * Adds the given section to the view.
     * @type {(function(*): void)|*}
     */
    const addSection = useCallback((sectionKey) => {
        setSections(prevState => PartInformationLookupSingleViewSections.filter(e => (prevState?.findIndex(f => Object.keys(f)[0] === Object.keys(e)[0]) !== -1) || Object.keys(e)[0] === sectionKey))
    }, [])

    /**
     * Checks if the given section is shown in the view.
     * @type {function(*): boolean}
     */
    const isShown = useCallback((sectionName) => {
        return !!sections?.find(e => Object.keys(e)[0] === sectionName);
    }, [sections])

    const showRelatedParts = useMemo(() => isShown(PartSections.relatedParts), [isShown]);
    const showFeedbacks = useMemo(() => isShown(PartSections.feedbacks), [isShown]);
    const showVisitLogs = useMemo(() => isShown(PartSections.visitLogs), [isShown]);
    const showInfo = useMemo(() => isShown(PartSections.info), [isShown]);


    /**
     * Fetches the price history specific for visual charts of the current product with the provided filters.
     *
     * @param {any} filters
     * @return {Promise<Partial<{dateLabel: string, data: [{formatValue: (amount: any, decimalCount?: number, decimal?: string, thousands?: string) => string, data: unknown[] | undefined, label: string}], resultFlag: boolean}>>}
     */
    const getProductPricesForChart = useCallback(async (filters) => {
        const forApi = {
            ...filters,
            partId: Number(part?.id),
        }
        const [response, priceLevelDict, getLivePriceLevelsResponse] = await Promise.all([
            Api.generateReportOfPartPriceLevels(forApi),
            getPriceLevelDictionary(),
            Api.getPartLivePriceLevels(part?.id),
        ]);
        if (!isMounted())
            return {
                resultFlag: false,
            };
        if (!response?.resultFlag || !getLivePriceLevelsResponse?.resultFlag) {
            return response;
        }
        const _data = [
            ...(getLivePriceLevelsResponse?.data?.sort((a, b) => a.priceLevel.levelIndex - b.priceLevel.levelIndex)?.map(e => ({
                submittedDate: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                newPrice: e.price,
                operatorName: "",
                priceLevel: {
                    ...e.priceLevel,
                    title: priceLevelDict?.find(d => d.priceLevelId === e.priceLevel?.id)?.title ?? e.priceLevel?.title
                },
            })) ?? []),
            ...(response?.data?.map(e => ({
                submittedDate: e.submittedDate.split('T')[0].concat(`T${e.submittedTime}`),
                newPrice: e.newPrice,
                operatorName: e.operatorName,
                priceLevel: {
                    ...e.priceLevel,
                    title: priceLevelDict?.find(d => d.priceLevelId === e.priceLevel.id)?.title ?? e.priceLevel?.title,
                },
            })) ?? [])
        ];
        const aggregatedPriceLevels =
            Object.fromEntries(
                Object
                    .entries(
                        _data.reduce((item, obj) => {
                            const key = obj.priceLevel.id;
                            return {...item, [key]: [...(item[key] ?? []), obj]}
                        }, {})
                    )
                    .map(([key, arr]) => [
                        key,
                        arr.map(e => ({
                            date: e.submittedDate,
                            entry: e.newPrice,
                            extra: {
                                operator: e.operatorName ?? '',
                                priceLevel: e.priceLevel,
                            },
                        }))
                    ])
            );
        const options = Array(Object.keys(aggregatedPriceLevels).length).fill(null).map((_, i) => ({
            borderColor: chartColors[i % chartColors.length],
            pointBackgroundColor: chartColors[i % chartColors.length],
            pointBorderColor: chartColors[i % chartColors.length],
            borderWidth: 1,
        }));
        return {
            resultFlag: response?.resultFlag,
            dateLabel: 'Date',
            dateExportOrderIndex: 2,
            data: Object.entries(aggregatedPriceLevels).map(([id, data], index) => ({
                data: data,
                id: Number(id),
                label: data?.find(e => e.extra?.priceLevel?.id === Number(id))?.extra?.priceLevel?.title ?? '',
                formatValue: formatMoney,
                options: options[index],
                exportOrderIndex: index + 2,
            })) ?? [],
            exportSpecific: [
                {
                    label: 'Operator',
                    valueGetter: (e) => e?.operator ?? '',
                    orderIndex: 0,
                }
            ],
        };
    }, [chartColors, getPriceLevelDictionary, isMounted, part?.id])

    return (
        <>
            <div className={`sticky-section no-top d-flex flex-column justify-content-center align-items-center`}>
                {
                    sections.map(section => (
                        <PartScrollButton
                            key={Object.keys(section)[0]}
                            section={section}
                            selected={Object.keys(section)[0] === selectedSection}
                            onSelect={scrollToSection}
                        />
                    ))
                }
            </div>
            <div className={'d-flex align-items-center justify-content-between'}>
                <div className={'d-flex align-items-center justify-content-start'} style={{gap: 8}}>
                    <IconButton
                        onPressed={() => history.goBack()}
                        color={'paper.secondary'}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <Typography variant={'h6'} fontWeight={600}>
                        Product Information
                    </Typography>
                </div>
                <div className={'d-flex align-items-center justify-content-end'} style={{gap: 8}}>
                    {
                        operatorAccessLevels.packages.sales.pricesInLookup &&
                        <Tooltip title={"View Price History"}>
                            <Box component={'span'}>
                                <IconButton
                                    disabled={!part?.id}
                                    onPressed={() => setOpenPriceHistoryChart(true)}
                                    color={'paper.secondary'}
                                >
                                    <QueryStatsRounded/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.lookup.reportCompetitorPrice &&
                        operatorAccessLevels.packages.sales.pricesInLookup &&
                        operatorAccessLevels.packages.base.customerList &&
                        <Tooltip title={t('reportCompetitorPrice')}>
                            <Box component={'span'}>
                                <IconButton
                                    disabled={!part?.id || !customer?.id}
                                    onPressed={reportCompetitorPrice}
                                    color={'paper.secondary'}
                                >
                                    <SvgIcon className={'icon large p-1'} component={Competitors}/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.sales.changeProductPricesInLookup &&
                        <Tooltip title={"Manage Product Prices"}>
                            <Box component={'span'}>
                                <IconButton
                                    disabled={!part?.id}
                                    onPressed={() => setShowPrices(true)}
                                    color={'paper.secondary'}
                                >
                                    <SvgIcon className={'icon large p-1'} component={Price}/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    }
                    {
                        <Tooltip title={"Report a Problem"}>
                            <Box component={'span'}>
                                <IconButton
                                    disabled={!part?.id}
                                    onPressed={reportProblem}
                                    color={'paper.secondary'}
                                >
                                    <SvgIcon className={'icon large p-1'} component={Report}/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.clientPlatform.shareProductLink &&
                        <Tooltip title={"Create a Shareable Link"}>
                            <Box component={'span'}>
                                <IconButton
                                    disabled={!part?.id}
                                    onPressed={() => setShowLink(true)}
                                    color={'paper.secondary'}
                                >
                                    <SvgIcon className={'icon large p-1'} component={Share}/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.lookup.productInformation &&
                        <Tooltip title={"Edit This Product"}>
                            <Box component={'span'}>
                                <IconButton
                                    disabled={!part?.id}
                                    color={'paper.secondary'}
                                    component={Link}
                                    to={routeFunctions.products.single(part?.id)}
                                    target={'_blank'}
                                >
                                    <EditOutlined className={'icon large p-1'}/>
                                </IconButton>
                            </Box>
                        </Tooltip>
                    }
                </div>
            </div>
            <div className='w-100 my-3'/>
            <Paper elevation={0} className={'d-flex flex-column w-100 align-items-center flex-grow-1 rounded-xxl p-3'}>
                <LookupProductInformationViewBasicSection
                    id={PartSections.basic}
                    className={'py-2'}
                    customer={customer}
                    setPart={setPart}
                    setLoading={setPartLoading}
                    setShowPrices={setShowPrices}
                    showPrices={showPrices}
                    removeSection={removeSection}
                    addSection={addSection}
                    partNumber={partNumber}
                />
                {
                    showInfo &&
                    <>
                        <div className='w-100 my-3'/>
                    </>
                }
                <LookupProductInformationViewInfoSection
                    id={PartSections.info}
                    className='py-2 min-vh-50'
                    part={part}
                    loading={partLoading}
                    removeSection={removeSection}
                    addSection={addSection}
                    industryId={part?.industryId}
                    shown={showInfo}
                />
                {
                    showVisitLogs &&
                    <>
                        <div className='w-100 my-3'/>
                        <Divider className={'divider'}/>
                    </>
                }
                <LookupProductInformationViewVisitLogsSection
                    id={PartSections.visitLogs}
                    className='py-3'
                    customerId={customer?.id}
                    partId={part?.id}
                    industryId={part?.industryId}
                    shown={showVisitLogs}
                    removeSection={removeSection}
                    addSection={addSection}
                />
                {
                    showRelatedParts &&
                    <>
                        <div className='w-100 my-3'/>
                        <Divider className={'divider'}/>
                        <div className='w-100 my-3'/>
                    </>
                }
                <LookupProductInformationViewRelatedPartsSection
                    id={PartSections.relatedParts}
                    idToListenTo={PartSections.info}
                    className='py-2 min-vh-50'
                    partNumber={partNumber}
                    removeSection={removeSection}
                    addSection={addSection}
                    shown={showRelatedParts}
                />
                {
                    showFeedbacks &&
                    <>
                        <div className='w-100 my-3'/>
                        <Divider className={'divider'}/>
                        <div className='w-100 my-3'/>
                    </>
                }
                <LookupProductInformationViewFeedbacksSection
                    id={PartSections.feedbacks}
                    idToListenTo={PartSections.info}
                    className='py-2 min-vh-50'
                    partNumber={partNumber}
                    removeSection={removeSection}
                    addSection={addSection}
                    shown={showFeedbacks}
                />
            </Paper>
            <div className='w-100 my-3'/>
            {
                operatorAccessLevels.packages.clientPlatform.shareProductLink &&
                <ManagePartShareLinkDialog
                    open={showLink}
                    setOpen={setShowLink}
                    partId={part?.id}
                />
            }
            <ReportProblemModal
                open={showProblem}
                setOpen={setShowProblem}
                partId={part?.id}
            />
            {
                operatorAccessLevels.packages.lookup.reportCompetitorPrice &&
                <ReportCompetitorPriceDialog
                    open={showCompetitorsPrice}
                    setOpen={setShowCompetitorsPrice}
                    part={{...part, partNumber}}
                    customer={customer}
                />
            }
            <ProductEntryHistoryDialog
                title={`Price History for ${partNumber}`}
                open={openPriceHistoryChart}
                close={() => setOpenPriceHistoryChart(false)}
                getHistory={getProductPricesForChart}
                hideExport
            />
        </>
    );
}

export default LookupProductInformationView;
